import React, { useContext } from 'react';
import { UseQueryResult, useQuery } from 'react-query';

import { useForceUpdate } from 'hooks/force-update';

import { useOnSessionObjectsCleared } from '../hooks';
import { useSession } from '../providers';
import { SessionEntityRequestHook } from '../types';
import { getSessionObjectKey } from './session-objects';
import { getLocalAuthenticationToken } from './token';

type SafeProviderProps = {
  /**
   * Component that will be rendered if this entity fails to load
   *
   * **WARNING**: don't use the `useSessionEntityQuery` inside the error
   * component or it will enter a infinite loop.
   */
  errorComponent: React.ComponentType<{ error: unknown }>;

  /**
   * Component that will be rendered while this entity is loading.
   *
   * **WARNING**: don't use the `useSessionEntityQuery` inside the error
   * component or it will enter a infinite loop.
   */
  loadingComponent: React.ComponentType;
};

type SessionEntityContextBag<SessionEntityType> = {
  QUERY_KEY: string;
  useSessionEntityData: () => SessionEntityType;
  useMaybeSessionEntityData: () => SessionEntityType | null | undefined;
  useSessionEntityQuery: () => UseQueryResult<SessionEntityType | null, string>;
  SessionEntityProvider: React.FC<{}>;
  SafeSessionEntityProvider: React.FC<SafeProviderProps>;
};

export function CreateSessionEntityContext<SessionEntityType>(
  sessionEntityKey: string,
  sessionEntityRequestHook: SessionEntityRequestHook<SessionEntityType>
): SessionEntityContextBag<SessionEntityType> {
  const SESSION_ENTITY_KEY = getSessionObjectKey(sessionEntityKey);

  type EntityContextType = SessionEntityType | undefined | null;

  const EntityContext = React.createContext<EntityContextType>(undefined);

  const useSessionEntityData = (): SessionEntityType => {
    const sessionEntity = useContext(EntityContext);

    if (!sessionEntity) {
      throw new Error(
        `useSessionEntityData must be used within a SessionEntityProvider or SafeSessionEntityProvider. Entity key: ${sessionEntityKey}`
      );
    }

    return sessionEntity;
  };

  const useMaybeSessionEntityData = (): EntityContextType => useContext(EntityContext);

  const useSessionEntityQuery = (): UseQueryResult<SessionEntityType | null, string> => {
    const accessToken = getLocalAuthenticationToken();

    const [isAuthenticated] = useSession();

    const [queryFn, queryEnabled] = sessionEntityRequestHook(accessToken);

    return useQuery(SESSION_ENTITY_KEY, queryFn, {
      enabled: isAuthenticated && queryEnabled,
      refetchOnWindowFocus: false,
      retry: false,
    });
  };

  const SessionEntityProvider: React.FC = ({ children }) => {
    const { data } = useSessionEntityQuery();

    const forceUpdate = useForceUpdate();

    useOnSessionObjectsCleared(forceUpdate);

    return <EntityContext.Provider value={data}>{children}</EntityContext.Provider>;
  };

  const SafeSessionEntityProvider: React.FC<SafeProviderProps> = ({
    children,
    loadingComponent: LoadingComponent,
    errorComponent: ErrorComponent,
  }) => {
    const { isError, isLoading, error, isSuccess, data } = useSessionEntityQuery();

    const forceUpdate = useForceUpdate();

    useOnSessionObjectsCleared(forceUpdate);

    if (isError) {
      return <ErrorComponent error={error} />;
    }

    if (isLoading) {
      return <LoadingComponent />;
    }

    if (isSuccess && data) {
      return <SessionEntityProvider>{children}</SessionEntityProvider>;
    }

    return <LoadingComponent />;
  };

  return {
    QUERY_KEY: SESSION_ENTITY_KEY,
    useSessionEntityData,
    useMaybeSessionEntityData,
    useSessionEntityQuery,
    SessionEntityProvider,
    SafeSessionEntityProvider,
  };
}
